import {
  TableBody as ShadcnTableBody,
  TableCell as ShadcnTableCell,
  TableRow as ShadcnTableRow,
} from '@shadcn/ui/table';
import { Row, flexRender, ColumnDef } from '@tanstack/react-table';
import React from 'react';

import { TABLE_CONSTANTS } from '../constants';

interface ITableBodyProps<TData> {
  readonly rows: Row<TData>[];
  readonly columns: ColumnDef<TData>[];
  readonly loading?: boolean;
  readonly renderSubRow?: (row: TData) => React.ReactNode;
}

export function TableBody<TData>({ rows, columns, loading, renderSubRow }: ITableBodyProps<TData>) {
  const { STYLING } = TABLE_CONSTANTS;

  if (loading || !rows.length) {
    return (
      <ShadcnTableBody>
        <ShadcnTableRow>
          <ShadcnTableCell className={`h-24 text-center ${STYLING.COLORS.TEXT}`} colSpan={columns.length}>
            {loading && 'Loading...'}
            {!loading && !rows.length && ' No results.'}
          </ShadcnTableCell>
        </ShadcnTableRow>
      </ShadcnTableBody>
    );
  }

  return (
    <ShadcnTableBody>
      {rows.map((row, rowIndex) => {
        const isLastRow = rowIndex === rows.length - 1;
        const isExpanded = Boolean(renderSubRow) && row.getIsExpanded();

        return (
          <React.Fragment key={row.id}>
            <ShadcnTableRow>
              {row.getVisibleCells().map((cell) => {
                const isExpanderColumn = cell.column.id === TABLE_CONSTANTS.EXPANDER_COLUMN_ID;

                return (
                  <ShadcnTableCell
                    className={`${STYLING.COLORS.TEXT} text-md break-words
                      ${STYLING.CELL_PADDING.TOP} ${STYLING.CELL_PADDING.BOTTOM}
                      ${
                        isExpanderColumn
                          ? `${STYLING.EXPANDER_COLUMN.WIDTH} ${STYLING.EXPANDER_COLUMN.PADDING}`
                          : `${STYLING.CELL_PADDING.LEFT} ${STYLING.CELL_PADDING.RIGHT}`
                      }
                      ${isLastRow && !isExpanded ? STYLING.BORDERS.CELL : ''}`}
                    key={cell.id}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </ShadcnTableCell>
                );
              })}
            </ShadcnTableRow>
            {isExpanded && (
              <ShadcnTableRow>
                <ShadcnTableCell
                  className={`${STYLING.COLORS.TEXT}
                    ${STYLING.CELL_PADDING.TOP} ${STYLING.CELL_PADDING.BOTTOM}
                    ${STYLING.CELL_PADDING.LEFT} ${STYLING.CELL_PADDING.RIGHT}
                    ${isLastRow ? STYLING.BORDERS.CELL : ''}`}
                  colSpan={columns.length}
                >
                  {renderSubRow?.(row.original)}
                </ShadcnTableCell>
              </ShadcnTableRow>
            )}
          </React.Fragment>
        );
      })}
    </ShadcnTableBody>
  );
}
